import { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { Building2, Check, ChevronDown, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

export default function PropertySwitcher() {
  const { user, setUserExternal } = useAuth();
  const isMaster = user?.role === "master_admin";
  const [hotels, setHotels] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newLoc, setNewLoc] = useState("India");
  const [active, setActive] = useState(user?.hotel_id);
  const [ownHotelName, setOwnHotelName] = useState("");

  async function load() {
    if (isMaster) {
      try {
        const { data } = await api.get("/hotels");
        setHotels(data.hotels);
        setActive(data.active_hotel_id);
      } catch { /* ignore */ }
    } else {
      try {
        const { data } = await api.get("/hotel/me");
        setOwnHotelName(data.name || "");
      } catch { /* ignore */ }
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function activate(h) {
    if (h.id === active) { setOpen(false); return; }
    try {
      await api.post(`/hotels/${h.id}/activate`);
      setActive(h.id);
      toast.success(`Switched to ${h.name}`);
      // refresh user context
      setUserExternal && setUserExternal({ ...user, hotel_id: h.id });
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Switch failed"); }
  }

  async function createHotel(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post("/hotels", { name: newName.trim(), location: newLoc || "India" });
      toast.success("Property added");
      setAdding(false);
      setNewName(""); setNewLoc("India");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  }

  // Property admin view: read-only display, no switcher, no add button.
  if (!isMaster) {
    return (
      <div className="w-full flex items-center gap-2 bg-[#12182B] border border-white/10 rounded-md px-3 py-2.5 text-sm" data-testid="property-display">
        <Building2 className="w-4 h-4 text-[#D4AF37] shrink-0" strokeWidth={1.5} />
        <span className="truncate">{ownHotelName || "My property"}</span>
      </div>
    );
  }

  const current = hotels.find(h => h.id === active) || { name: "Property" };

  return (
    <>
      <div className="relative" data-testid="property-switcher">
        <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2 bg-[#12182B] border border-white/10 hover:border-[#D4AF37]/30 rounded-md px-3 py-2.5 text-sm transition" data-testid="property-switcher-trigger">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-4 h-4 text-[#D4AF37] shrink-0" strokeWidth={1.5} />
            <span className="truncate">{current.name}</span>
          </div>
          <ChevronDown className={`w-3.5 h-3.5 transition ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute left-0 right-0 top-full mt-2 z-20 bg-[#0a0f1c] border border-white/10 rounded-md shadow-xl overflow-hidden" data-testid="property-switcher-menu">
            {hotels.map((h) => (
              <button key={h.id} onClick={() => activate(h)} className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm hover:bg-white/5 transition text-left" data-testid={`property-option-${h.id}`}>
                <div className="min-w-0">
                  <div className="truncate">{h.name}</div>
                  <div className="text-[10px] text-white/40 truncate">{h.location}</div>
                </div>
                {h.id === active && <Check className="w-4 h-4 text-[#D4AF37]" />}
              </button>
            ))}
            <button onClick={() => { setAdding(true); setOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm border-t border-white/10 text-[#D4AF37] hover:bg-[#D4AF37]/5" data-testid="add-property-btn">
              <Plus className="w-3.5 h-3.5" /> Add property
            </button>
          </div>
        )}
      </div>
      {adding && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setAdding(false)} data-testid="add-property-dialog">
          <form onClick={(e) => e.stopPropagation()} onSubmit={createHotel} className="bg-[#12182B] border border-white/10 rounded-xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-5">
              <div className="font-display text-xl">Add new property</div>
              <button type="button" onClick={() => setAdding(false)} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Property name</label>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} required className="w-full bg-[#0a0f1c] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/60" data-testid="new-property-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Location</label>
                <input value={newLoc} onChange={(e) => setNewLoc(e.target.value)} className="w-full bg-[#0a0f1c] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/60" data-testid="new-property-location" />
              </div>
            </div>
            <button type="submit" className="btn-gold w-full rounded-md py-2.5 mt-6 text-sm" data-testid="new-property-submit">Add property</button>
          </form>
        </div>
      )}
    </>
  );
}
